/**
 * Plan catalog for the frontend.
 *
 * The BACKEND is the source of truth (backend/config/plans.js, served at
 * GET /api/plans). This file mirrors its shape and ships a fallback copy so the
 * public pricing page paints instantly and still renders if the API is down.
 *
 * The previous pricing lived hardcoded in two separate React files with TND
 * prices that matched nothing the server enforced — this indirection exists
 * specifically so that cannot happen again.
 */

export type PlanId = "free_trial" | "starter" | "pro" | "custom";
export type BillingPeriod = "month" | "one_time" | "custom";
export type LimitPeriod = "total" | "month";

/** Matches backend/config/plans.js UNLIMITED. */
export const UNLIMITED = -1;

export interface Plan {
  id: PlanId;
  name: string;
  tagline: string;
  priceUsd: number | null; // null => "Contact us"
  billingPeriod: BillingPeriod;
  commentLimit: number; // -1 => unlimited
  commentPeriod: LimitPeriod;
  pageLimit: number; // -1 => unlimited
  trialDays: number | null;
  ctaKind: "signup" | "contact";
  popular: boolean;
  features: string[];
}

export interface PlanCatalog {
  currency: string;
  features: string[];
  plans: Plan[];
}

const SHARED_FEATURES = [
  "AI comment moderation",
  "Spam detection",
  "Offensive & toxic comment detection",
  "Tunisian Derja, Arabic, French & English",
  "Custom moderation rules",
  "Automatic public replies",
  "Automatic private replies",
  "Facebook integration",
  "Instagram integration",
];

export const PLAN_CATALOG_FALLBACK: PlanCatalog = {
  currency: "USD",
  features: SHARED_FEATURES,
  plans: [
    {
      id: "free_trial",
      name: "Free Trial",
      tagline: "Try Moujib on one page, no card required.",
      priceUsd: 0,
      billingPeriod: "one_time",
      commentLimit: 1000,
      commentPeriod: "total",
      pageLimit: 1,
      trialDays: 14,
      ctaKind: "signup",
      popular: false,
      features: SHARED_FEATURES,
    },
    {
      id: "starter",
      name: "Starter",
      tagline: "For a growing page.",
      priceUsd: 19,
      billingPeriod: "month",
      commentLimit: 10000,
      commentPeriod: "month",
      pageLimit: 3,
      trialDays: null,
      ctaKind: "signup",
      popular: false,
      features: SHARED_FEATURES,
    },
    {
      id: "pro",
      name: "Pro",
      tagline: "For busy pages and small agencies.",
      priceUsd: 49,
      billingPeriod: "month",
      commentLimit: 100000,
      commentPeriod: "month",
      pageLimit: 10,
      trialDays: null,
      ctaKind: "signup",
      popular: true,
      features: SHARED_FEATURES,
    },
    {
      id: "custom",
      name: "Custom",
      tagline: "Volume pricing, tailored limits.",
      priceUsd: null,
      billingPeriod: "custom",
      commentLimit: UNLIMITED,
      commentPeriod: "month",
      pageLimit: UNLIMITED,
      trialDays: null,
      ctaKind: "contact",
      popular: false,
      features: SHARED_FEATURES,
    },
  ],
};

const numberFormat = new Intl.NumberFormat("en-US");

export function formatNumber(n: number): string {
  return numberFormat.format(n);
}

export function formatPrice(plan: Plan): { amount: string; suffix: string } {
  if (plan.priceUsd === null) return { amount: "Contact us", suffix: "" };
  if (plan.priceUsd === 0) return { amount: "$0", suffix: "" };
  return {
    amount: `$${plan.priceUsd}`,
    suffix: plan.billingPeriod === "month" ? "/month" : "",
  };
}

export function formatCommentLimit(plan: Plan): string {
  if (plan.commentLimit === UNLIMITED) return "Unlimited comments";
  const n = formatNumber(plan.commentLimit);
  return plan.commentPeriod === "total"
    ? `${n} comments total`
    : `${n} comments / month`;
}

export function formatPageLimit(plan: Plan): string {
  if (plan.pageLimit === UNLIMITED) return "Unlimited pages";
  return plan.pageLimit === 1 ? "1 page" : `${plan.pageLimit} pages`;
}

/**
 * The bullet list shown on a pricing card. Limits are DERIVED from the same
 * numbers the backend enforces, so the copy cannot drift from reality.
 */
export function planBullets(plan: Plan): string[] {
  const bullets = [formatPageLimit(plan), formatCommentLimit(plan)];
  if (plan.trialDays) bullets.push(`${plan.trialDays}-day trial`);
  return bullets.concat(plan.features);
}

export function planCtaLabel(plan: Plan): string {
  if (plan.ctaKind === "contact") return "Contact us";
  if (plan.id === "free_trial") return "Start free";
  return `Choose ${plan.name}`;
}

export function getPlan(catalog: PlanCatalog, id: PlanId): Plan | undefined {
  return catalog.plans.find((p) => p.id === id);
}

/** The upgrade ladder. Custom is the top — there is nothing to suggest beyond it. */
const UPGRADE_PATH: Record<PlanId, PlanId | null> = {
  free_trial: "starter",
  starter: "pro",
  pro: "custom",
  custom: null,
};

/** The plan we'd suggest someone move up to, or undefined if they're at the top. */
export function getNextPlan(catalog: PlanCatalog, current: PlanId): Plan | undefined {
  const nextId = UPGRADE_PATH[current];
  return nextId ? getPlan(catalog, nextId) : undefined;
}

/**
 * What the next tier actually buys, phrased as concrete gains rather than
 * feature names — every plan has the same features, only the volume differs.
 */
export function upgradeGains(current: Plan, next: Plan): string[] {
  const gains: string[] = [];

  if (next.commentLimit === UNLIMITED) {
    gains.push("Unlimited comments");
  } else if (current.commentLimit !== UNLIMITED && next.commentLimit > current.commentLimit) {
    const times = Math.round(next.commentLimit / Math.max(1, current.commentLimit));
    gains.push(
      times >= 2
        ? `${times}× more comments (${formatNumber(next.commentLimit)} / month)`
        : `${formatNumber(next.commentLimit)} comments / month`
    );
  }

  if (next.pageLimit === UNLIMITED) {
    gains.push("Unlimited pages");
  } else if (current.pageLimit !== UNLIMITED && next.pageLimit > current.pageLimit) {
    const extra = next.pageLimit - current.pageLimit;
    gains.push(`${extra} more page${extra === 1 ? "" : "s"} (${next.pageLimit} total)`);
  }

  return gains;
}
